import { create } from "zustand";

export type ImportItemStatus =
  "queued" | "importing" | "needs_settings" | "done" | "failed" | "rejected";

export interface ImportItem {
  id: number;
  file: File;
  /** The model this file was dropped into. Carried per item, never read from
   *  the current route: a run outlives the page the user is looking at. The
   *  pump runs only the rows matching `mountedModelId`, because the table is
   *  created under this id but attached by whichever canvas is mounted when
   *  the reply lands — so running one model's files under another splits them. */
  modelId: string;
  name: string;
  size: number;
  status: ImportItemStatus;
  /** Why it stopped — the row carries it, so nothing toasts per file. */
  message?: string;
  /** 0..100 while importing. */
  progress?: number;
  attempts: number;
  /** Already counted in an end-of-run summary; the next run's is not about it. */
  reported?: boolean;
}

/** State only, so the worker-operation hook can ask who owns a filename
 *  without importing the pump (which imports that hook back). */
export const useImportQueueStore = create<{
  items: ImportItem[];
  /** The canvas on screen, so a row can say it belongs to another model
   *  rather than offering a Review that would import into this one. */
  mountedModelId: string | null;
  /** The upload panel holds a file; Review would replace it, so it waits. */
  panelFileOpen: boolean;
}>(() => ({
  items: [],
  mountedModelId: null,
  panelFileOpen: false,
}));

export const setImportPanelFileOpen = (open: boolean) =>
  useImportQueueStore.setState({ panelFileOpen: open });

export const setImportItems = (fn: (items: ImportItem[]) => ImportItem[]) =>
  useImportQueueStore.setState((s) => ({ items: fn(s.items) }));

export const patchImportItem = (id: number, next: Partial<ImportItem>) =>
  setImportItems((items) =>
    items.map((i) => (i.id === id ? { ...i, ...next } : i)),
  );

/** True while the queue is answering for this file: it reports the outcome in
 *  the file's own row, so the shared upload toast must stay quiet. Only the row
 *  actually in flight can claim a name — the worker protocol keys replies by
 *  filename alone, so the narrower the claim, the fewer messages we can steal
 *  from an unrelated import of a same-named file. */
export function importQueueOwns(filename: string): boolean {
  return useImportQueueStore
    .getState()
    .items.some((i) => i.name === filename && i.status === "importing");
}

/** Anything that still needs the panel — work to run, or a decision to take.
 *  A failed or rejected row wants nothing from it, so one left un-dismissed
 *  must not pin every later import to this tab. */
export function importQueueHasPending(): boolean {
  return useImportQueueStore
    .getState()
    .items.some(
      (i) =>
        i.status === "queued" ||
        i.status === "importing" ||
        i.status === "needs_settings",
    );
}
